import type { CrewId } from '@/versions/v1/schema/seat';
import type { Case } from '@/versions/v1/schema/case';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { addMinutes, clockOnProductionDay, diffMinutes, shiftOf } from '@/versions/v1/shared/clock.derive';
import { activeCrewForShift } from '@/versions/v1/shared/bunker-hill-org-seed';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { seriesAt, windowMean } from '@/versions/v1/shared/series.derive';
import { casesForRole, isMaintenanceSeatCase } from '@/versions/v1/shared/role-scope';

export type CrewLadderStep = {
  label: string;
  value: string;
};

export type CrewComparisonPanel = {
  crewId: CrewId;
  crewLabel: string;
  shortStaffNote?: string;
  crewEffectPt?: number;
  ladder: readonly CrewLadderStep[];
};

export type CrewComparisonTable = {
  crewLabels: readonly string[];
  rows: readonly { label: string; values: readonly string[] }[];
  shortStaffNote?: string;
};

const CREW_IDS: readonly CrewId[] = ['crew-a', 'crew-b', 'crew-c', 'crew-d'];
const TRAILING_DAYS = 14;
const FEED_COEFF = 1.8;
const REAGENT_COEFF = 0.4;
const EQUIP_COEFF = 0.5;

function resolveRaisedAt(raisedAt: string, cursor: string): string {
  return raisedAt.includes('T') ? raisedAt : clockOnProductionDay(raisedAt, cursor);
}

function openMechCasesAtShiftStart(
  cases: readonly Case[],
  shiftStart: string,
  cursor: string,
): number {
  return cases.filter(
    (c) =>
      isMaintenanceSeatCase(c) &&
      diffMinutes(resolveRaisedAt(c.raisedAt, cursor), shiftStart) <= 0 &&
      !['approved', 'rejected', 'closed'].includes(c.state),
  ).length;
}

function crewShiftsInWindow(
  state: SessionState,
  crewId: CrewId,
): { pbRec: number; stamp: string; kind: 'day' | 'night' }[] {
  const cursor = state.cursor;
  const windowStart = addMinutes(cursor, -TRAILING_DAYS * 24 * 60);
  const rows: { pbRec: number; stamp: string; kind: 'day' | 'night' }[] = [];
  let t = windowStart;
  while (diffMinutes(t, cursor) <= 0) {
    const { kind, start } = shiftOf(t);
    const crew = activeCrewForShift(start, kind);
    if (crew.crewId === crewId) {
      const view = seriesAt('rs-pb-rec-shift', { ...state, cursor: start });
      const val = view.latest?.value;
      if (val !== undefined) {
        rows.push({ pbRec: val, stamp: start, kind });
      }
    }
    t = addMinutes(t, 12 * 60);
  }
  return rows;
}

function formatPct(v: number): string {
  return `${v.toFixed(1)}%`;
}

type CrewLadderNumbers = {
  crewId: CrewId;
  crewLabel: string;
  shortStaffNote?: string;
  raw: number;
  afterFeed: number;
  afterReagent: number;
  afterEquip: number;
};

function crewLadderNumbers(
  state: SessionState,
  cases: readonly Case[],
  crewId: CrewId,
): CrewLadderNumbers | undefined {
  const shifts = crewShiftsInWindow(state, crewId);
  if (shifts.length === 0) return undefined;

  const windowStart = addMinutes(state.cursor, -TRAILING_DAYS * 24 * 60);
  const headPbMean = windowMean('rs-head-pb', state, windowStart);
  const aeroDesign = readingByIdLookup('rd-reag-aero')?.design ?? 35;
  const openCases = casesForRole('superintendent', cases);

  let feedTotal = 0;
  let reagentTotal = 0;
  let equipTotal = 0;
  let shortStaffNote: string | undefined;

  for (const row of shifts) {
    const head =
      seriesAt('rs-head-pb', { ...state, cursor: row.stamp }).latest?.value ?? headPbMean;
    feedTotal += FEED_COEFF * ((head - headPbMean) / 0.1);

    const aero =
      seriesAt('rs-reag-aero', { ...state, cursor: row.stamp }).latest?.value ?? aeroDesign;
    const underPct = Math.max(0, ((aeroDesign - aero) / aeroDesign) * 100);
    reagentTotal += REAGENT_COEFF * (underPct / 10);

    equipTotal += openMechCasesAtShiftStart(openCases, row.stamp, state.cursor) * EQUIP_COEFF;

    const crew = activeCrewForShift(row.stamp, row.kind);
    if (crew.crewId === crewId && crew.headcount < 6) {
      shortStaffNote = `${crewId.replace('crew-', '').toUpperCase()} · ${crew.headcount} on · ${crew.seniors} senior`;
    }
  }

  const rawPb = shifts.reduce((s, r) => s + r.pbRec, 0) / shifts.length;
  const feedNorm = feedTotal / shifts.length;
  const reagentNorm = reagentTotal / shifts.length;
  const equipNorm = equipTotal / shifts.length;

  return {
    crewId,
    crewLabel: crewId.replace('crew-', '').toUpperCase(),
    shortStaffNote,
    raw: rawPb,
    afterFeed: rawPb - feedNorm,
    afterReagent: rawPb - feedNorm - reagentNorm,
    afterEquip: rawPb - feedNorm - reagentNorm - equipNorm,
  };
}

export function crewComparisonRead(
  state: SessionState,
  cases: readonly Case[],
): readonly CrewComparisonPanel[] {
  const numbers = CREW_IDS.map((id) => crewLadderNumbers(state, cases, id));
  const afterEquipValues = numbers.filter((n): n is CrewLadderNumbers => Boolean(n)).map((n) => n.afterEquip);
  const meanAfterEquip =
    afterEquipValues.length > 0
      ? afterEquipValues.reduce((s, v) => s + v, 0) / afterEquipValues.length
      : 0;

  return CREW_IDS.map((crewId, index) => {
    const row = numbers[index];
    if (!row) {
      return {
        crewId,
        crewLabel: crewId.replace('crew-', '').toUpperCase(),
        ladder: [
          { label: 'raw', value: '—' },
          { label: 'feed', value: '—' },
          { label: 'reagent', value: '—' },
          { label: 'equipment', value: '—' },
          { label: 'crew effect', value: '—' },
        ],
      };
    }
    const crewEffect = row.afterEquip - meanAfterEquip;
    return {
      crewId: row.crewId,
      crewLabel: row.crewLabel,
      shortStaffNote: row.shortStaffNote,
      crewEffectPt: crewEffect,
      ladder: [
        { label: 'raw', value: formatPct(row.raw) },
        { label: 'feed', value: formatPct(row.afterFeed) },
        { label: 'reagent', value: formatPct(row.afterReagent) },
        { label: 'equipment', value: formatPct(row.afterEquip) },
        {
          label: 'crew effect',
          value: `${crewEffect >= 0 ? '+' : ''}${crewEffect.toFixed(1)} pt`,
        },
      ],
    };
  });
}

/** Sum of absolute crew-effect recovery points across crews — staffing bucket input. */
export function crewStaffingShortfallPt(
  state: SessionState,
  cases: readonly Case[],
): number {
  const panels = crewComparisonRead(state, cases);
  return panels.reduce((sum, panel) => sum + Math.abs(panel.crewEffectPt ?? 0), 0);
}

export function crewComparisonTable(
  state: SessionState,
  cases: readonly Case[],
): CrewComparisonTable {
  const panels = crewComparisonRead(state, cases);
  const shortStaffNote = panels.find((p) => p.shortStaffNote)?.shortStaffNote;
  const ladderLabels = ['raw', 'feed', 'reagent', 'equipment', 'crew effect'] as const;
  return {
    crewLabels: panels.map((p) => p.crewLabel),
    shortStaffNote,
    rows: ladderLabels.map((label) => ({
      label,
      values: panels.map((p) => p.ladder.find((s) => s.label === label)?.value ?? '—'),
    })),
  };
}
